import { ReactNode, useState, useRef, useEffect } from "react";

interface MenuItem {
  icon: ReactNode;
  text: string;
  onClick: (e: React.MouseEvent) => void;
}

interface EllipsisMenuProps {
  items: MenuItem[];
}

const EllipsisMenu: React.FC<EllipsisMenuProps> = ({ items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <button
        type="button"
        className="p-2 text-gray-600 hover:text-gray-800 focus:outline-none"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        aria-label="Menu options"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 min-w-[16rem] whitespace-nowrap bg-white border border-[#D9D8E1] rounded-lg shadow-xl overflow-hidden z-50"
          ref={dropdownRef}
        >
          <div className="py-2 w-auto">
            {items.map((item, index) => (
              <div
                key={index}
                className="px-4 py-2 flex items-center space-x-3 text-[#48455B] hover:bg-gray-200 cursor-pointer transition-colors duration-150 mx-2"
                onClick={(e) => {
                  item.onClick(e);
                  setIsOpen(false);
                }}
              >
                {item.icon}
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EllipsisMenu;
